/**
 * The dataset's sources with status; a row expands into SourceDetail. Sources awaiting
 * review can be selected and approved or rejected together, with the total estimated
 * indexing cost shown first (#805). A possible duplicate is flagged on its row.
 * Each source shows its kind (fact / policy / opinion / testimony), and the list filters by it (#891).
 */
import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Copy } from 'lucide-react';
import { Button, Actions, StatusLine } from '../../../components/InstanceSettings/formPrimitives';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { useConfirm } from '../../../components/DialogProvider';
import SourceDetail from './SourceDetail';
import { errorKey, bulkSources, REVIEWABLE_STATUSES, SOURCE_KINDS, isSearchLink } from './datasetApi';
import { Table, TableScroll, StatusChip, KindChip, Lead, Meta } from './styles';

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
`;

const FilterButton = styled.button`
  padding: 4px 12px;
  border-radius: 14px;
  font-size: 12px;
  cursor: pointer;
  color: ${(p) => (p.$active ? '#fff' : '#aaa')};
  background: ${(p) => (p.$active ? 'rgba(147, 51, 234, 0.35)' : 'transparent')};
  border: 1px solid ${(p) => (p.$active ? '#c084fc' : 'rgba(255, 255, 255, 0.15)')};
`;

const Missing = styled.span`
  color: #888;
  font-style: italic;
`;

const BulkBar = styled(Actions)`
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(147, 51, 234, 0.08);
  border: 1px solid rgba(147, 51, 234, 0.25);
`;

const DuplicateFlag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  font-size: 11px;
  color: #fbbf24;
`;

function qualityLabel(q) {
  const score = q?.credibility_score;
  return typeof score === 'number' ? score.toFixed(2) : '–';
}

export default function SourceTable({ datasetId, sources, canApprove, canContribute, onChanged }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [kind, setKind] = useState('all');

  const shown = useMemo(
    () => (kind === 'all' ? sources : sources.filter((s) => (s.source_kind || 'none') === kind)),
    [sources, kind]
  );
  const kindCounts = useMemo(() => sources.reduce((m, s) => {
    const k = s.source_kind || 'none';
    return { ...m, [k]: (m[k] || 0) + 1 };
  }, {}), [sources]);
  const reviewable = useMemo(() => shown.filter((s) => REVIEWABLE_STATUSES.has(s.status)), [shown]);
  // Selection survives polling, but only for sources still awaiting review.
  const chosen = reviewable.filter((s) => selected.has(s.id));
  const total = chosen.reduce((sum, s) => sum + (Number(s.index_estimate_usd) || 0), 0);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const runBulk = async (action) => {
    if (action === 'reject' && !(await confirm({ message: t('datasetWorkspace.bulk.confirmReject', { count: chosen.length }), destructive: true }))) return;
    setBusy(true);
    setMessage(null);
    try {
      const r = await bulkSources(datasetId, action, chosen.map((s) => s.id));
      setSelected(new Set());
      setMessage({ ok: true, text: t(`datasetWorkspace.bulk.${action}Done`, { count: r.done }) });
      onChanged();
    } catch (err) {
      setMessage({ ok: false, text: err.message });
    } finally {
      setBusy(false);
    }
  };

  if (!sources.length) return <Lead>{t('datasetWorkspace.noSources')}</Lead>;

  return (
    <>
      <FilterBar role="group" aria-label={t('datasetWorkspace.kind')}>
        {['all', ...SOURCE_KINDS, 'none'].filter((k) => k === 'all' || kindCounts[k]).map((k) => (
          <FilterButton key={k} type="button" $active={kind === k} aria-pressed={kind === k} onClick={() => setKind(k)}>
            {t(`datasetWorkspace.kinds.${k}`)} ({k === 'all' ? sources.length : kindCounts[k]})
          </FilterButton>
        ))}
      </FilterBar>
      {canApprove && reviewable.length > 0 && (
        <BulkBar>
          <Button $size="small" onClick={() => setSelected(new Set(reviewable.map((s) => s.id)))}>
            {t('datasetWorkspace.bulk.selectAll', { count: reviewable.length })}
          </Button>
          {chosen.length > 0 && (
            <>
              <Button $size="small" $variant="primary" disabled={busy} onClick={() => runBulk('approve')}>
                {t('datasetWorkspace.bulk.approve', { count: chosen.length, cost: total.toFixed(2) })}
              </Button>
              <Button $size="small" disabled={busy} onClick={() => runBulk('reject')}>
                {t('datasetWorkspace.bulk.reject', { count: chosen.length })}
              </Button>
              <Button $size="small" $variant="ghost" onClick={() => setSelected(new Set())}>{t('datasetWorkspace.bulk.clear')}</Button>
            </>
          )}
          {message && <StatusLine $error={!message.ok}>{message.text}</StatusLine>}
        </BulkBar>
      )}
      <TableScroll>
        <Table>
          <thead>
            <tr>
              {canApprove && <th aria-label={t('datasetWorkspace.bulk.select')} />}
              <th>{t('datasetWorkspace.sourceName')}</th>
              <th>{t('datasetWorkspace.kind')}</th>
              <th>{t('datasetWorkspace.publisher')}</th>
              <th>{t('datasetWorkspace.link')}</th>
              <th>{t('datasetWorkspace.status')}</th>
              <th>{t('datasetWorkspace.quality')}</th>
              <th>{t('datasetWorkspace.chunks')}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((s) => (
              <React.Fragment key={s.id}>
                <tr data-clickable onClick={() => setOpenId(openId === s.id ? null : s.id)}>
                  {canApprove && (
                    <td onClick={(e) => e.stopPropagation()}>
                      {REVIEWABLE_STATUSES.has(s.status) && (
                        <input type="checkbox" aria-label={t('datasetWorkspace.bulk.select')}
                          checked={selected.has(s.id)} onChange={() => toggle(s.id)} />
                      )}
                    </td>
                  )}
                  <td>
                    {s.title || <Missing>{s.filename || '–'}</Missing>}
                    {s.duplicates?.length > 0 && (
                      <DuplicateFlag title={s.duplicates.map((d) => d.title).join(', ')}>
                        <Copy size={11} /> {t('datasetWorkspace.duplicates.flag')}
                      </DuplicateFlag>
                    )}
                  </td>
                  <td>{s.source_kind ? <KindChip $kind={s.source_kind}>{t(`datasetWorkspace.kinds.${s.source_kind}`)}</KindChip> : '–'}</td>
                  <td>{s.publisher || '–'}</td>
                  <td>
                    {s.link
                      ? (
                        <a href={s.link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                          {isSearchLink(s) ? t('datasetWorkspace.searchLink') : s.link}
                        </a>
                      )
                      : '–'}
                  </td>
                  <td>
                    <StatusChip $status={s.status}>
                      {s.status === 'failed' ? t(errorKey(s)) : t(`datasetWorkspace.statuses.${s.status}`)}
                    </StatusChip>
                  </td>
                  <td>{qualityLabel(s.source_quality)}</td>
                  <td>{s.chunk_count || '–'}</td>
                </tr>
                {openId === s.id && (
                  <tr>
                    <td colSpan={canApprove ? 8 : 7}>
                      {s.duplicates?.length > 0 && (
                        <Meta style={{ color: '#fbbf24', marginBottom: 8 }}>
                          {t('datasetWorkspace.duplicates.detail', { titles: s.duplicates.map((d) => d.title).join(', ') })}
                        </Meta>
                      )}
                      <SourceDetail datasetId={datasetId} source={s} canApprove={canApprove}
                        canContribute={canContribute} onChanged={onChanged} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      </TableScroll>
    </>
  );
}
